import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CarTileWrapper } from '../../../../pages/home/hero/car-tile/CarTileWrapper';
import useAppState from '../../../../common/state/appState';
import { shiftUnavailableVehiclesToEnd } from '../../../../pages/home/utilities/helpers';
import { IVehicleTile } from '../../../../types/vehicleTile';
import BaseCarousel from './BaseCarousel';
import { RelatedSearchesTile } from './subcomponents/RelatedSearchesTile';
import CircularProgress from '@mui/material/CircularProgress';
import { StyledButton, NavContainer, HeaderButtonsStack, StyledBox } from './styledComponents';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { Swiper as SwiperType } from 'swiper';
import { ANALYTICS_CONSTANTS, trackAnchorClick, trackEDDL } from '../../../../utilities/analytics';
import { ITrackingPayload } from '../../../../types/analytics';
import IconButton from '@mui/material/IconButton';
import KmxChevronLeftFilled from '@kmx/mui-icons/KmxChevronLeftFilled';
import KmxChevronRightFilled from '@kmx/mui-icons/KmxChevronRightFilled';

interface ICarouselProps {
    isLoading?: boolean;
    carouselId: string;
    placeholder?: {
        cardText: string;
        buttonText: string;
    };
    vehiclesData: IVehicleTile[];
    isRemoveEnabled?: boolean;
    onRemove?: (vehicle: IVehicleTile) => void;
    nextFocus?: string;
    isInsideHomeBase?: boolean;
    sectionName: string;
    showRelatedSearchesTile?: boolean;
    recommenderCohortId?: string;
    recommenderTestId?: string;
    disableNavButtons?: boolean;
    title?: string | ReactNode;
    searchToken?: string;
    buttonText?: string;
    buttonHref?: string;
    showButton?: boolean;
    taggingPosition?: string;
}

const UftCarTilesCarousel = ({
    isLoading = false,
    carouselId,
    vehiclesData,
    isRemoveEnabled,
    onRemove,
    nextFocus,
    isInsideHomeBase,
    sectionName,
    showRelatedSearchesTile,
    recommenderCohortId,
    recommenderTestId,
    disableNavButtons,
    title,
    showButton,
    buttonText = 'Show more',
    buttonHref,
    taggingPosition,
}: ICarouselProps) => {
    /**
     * Using useRef for Swiper navigation buttons in React allows
     * direct manipulation of the DOM elements for custom navigation,
     * which is essential when buttons are located outside the main
     * Swiper component or in multi-instance scenarios.
     */
    const nextButton = useRef(null);
    const prevButton = useRef(null);
    const swiperRef = useRef<SwiperType | null>(null);
    const [isPrevDisabled, setIsPrevDisabled] = useState(true);
    const [isNextDisabled, setIsNextDisabled] = useState(false);
    const [showNavigation, setShowNavigation] = useState(true);

    const [navigationParams, setNavigationParams] = useState({
        enabled: true,
        nextEl: null,
        prevEl: null,
    });

    const handleSwiperStateUpdates = useCallback((swiper: SwiperType) => {
        // Normally I would not put this in state, but the isBeginning and isEnd changing causes React to rerender the buttons
        // Which breaks the event listeners that Swiper attaches to the buttons, causing them to stop working after reaching the beginning or end of the carousel
        setShowNavigation(!(swiper.isBeginning && swiper.isEnd));
        setIsPrevDisabled(swiper.isBeginning);
        setIsNextDisabled(swiper.isEnd);
    }, []);

    const handleSwiperBeforeInit = useCallback(() => {
        setNavigationParams({
            enabled: true,
            nextEl: nextButton.current,
            prevEl: prevButton.current,
        });
    }, []);

    const handleSwiper = useCallback(
        (swiper: SwiperType) => {
            swiperRef.current = swiper;
            handleSwiperStateUpdates(swiper);
        },
        [handleSwiperStateUpdates],
    );

    const handleSlideChange = useCallback(handleSwiperStateUpdates, [handleSwiperStateUpdates]);

    const handleShowMoreButtonTagging = (event: React.MouseEvent<HTMLElement>) => {
        trackAnchorClick({
            clickEvent: event,
            taggingEvent: ANALYTICS_CONSTANTS.EVENTS.CLICK.SEARCH_INITIATED,
            title: buttonText,
            position: taggingPosition ?? sectionName,
            href: buttonHref as string,
            page: 'Homepage',
        });
    };

    const handleArrowButtonTagging = (direction: string) => {
        const payload: ITrackingPayload = {
            event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
            linkDetails: {
                name: direction,
                position: taggingPosition,
            },
        };
        trackEDDL(payload);
    };

    const getFbsDataForStockNumbers = useAppState((state) => state.getFbsDataForStockNumbers);
    const setFavoriteVehicles = useAppState((state) => state.setFavoriteVehicles);

    const sortedVehicleList = useMemo<IVehicleTile[]>(
        () => shiftUnavailableVehiclesToEnd(vehiclesData),
        [vehiclesData],
    );

    const allStockNumbers = useMemo(() => sortedVehicleList.map((vehicle) => vehicle.stockNumber), [sortedVehicleList]);

    const vehicleCount = vehiclesData.length;

    const saleableVehicleCount = useMemo(
        () => vehiclesData.filter((item: IVehicleTile) => item.isSaleable).length,
        [vehiclesData],
    );

    const handleRemove = useCallback(
        (vehicle: IVehicleTile) => {
            const remove = vehiclesData.find((v) => v.stockNumber === vehicle.stockNumber);
            if (remove && typeof onRemove === 'function') {
                onRemove(remove);
            }
        },
        [onRemove, vehiclesData],
    );

    const carouselItems = useMemo(() => {
        const items = sortedVehicleList
            .filter((item) => item !== null && Boolean(item))
            .map((vehicle: IVehicleTile, index: number) => {
                const { stockNumber }: IVehicleTile = vehicle;
                return (
                    <div className="carousel__slide" key={`${stockNumber}-slide-${index}`}>
                        <div className="carousel-slide-item">
                            <CarTileWrapper
                                vehicle={vehicle as IVehicleTile}
                                updatedViewedTiles={handleRemove}
                                enableRemove={isRemoveEnabled ? true : false}
                                allStockNumbers={allStockNumbers}
                                carouselId={carouselId}
                                sectionName={sectionName}
                                recommenderCohortId={recommenderCohortId}
                                recommenderTestId={recommenderTestId}
                                taggingPosition={taggingPosition}
                            />
                        </div>
                    </div>
                );
            });

        if (showRelatedSearchesTile) {
            items.push(<RelatedSearchesTile key="related-searches-tile" />);
        }

        return items;
    }, [
        allStockNumbers,
        carouselId,
        handleRemove,
        isRemoveEnabled,
        recommenderCohortId,
        recommenderTestId,
        sectionName,
        showRelatedSearchesTile,
        sortedVehicleList,
        taggingPosition,
    ]);

    useEffect(() => {
        setFavoriteVehicles();
    }, [setFavoriteVehicles]);

    useEffect(() => {
        getFbsDataForStockNumbers(vehiclesData.map(({ stockNumber }: { stockNumber: number }) => stockNumber));
    }, [getFbsDataForStockNumbers, vehiclesData]);

    useEffect(() => {
        if (!swiperRef.current) {
            return;
        }

        swiperRef.current.update();

        if (nextButton.current && prevButton.current && swiperRef.current.navigation) {
            swiperRef.current.navigation.destroy();
            swiperRef.current.params.navigation = {
                ...(typeof swiperRef.current.params.navigation === 'object' ? swiperRef.current.params.navigation : {}),
                enabled: true,
                nextEl: nextButton.current,
                prevEl: prevButton.current,
            };
            swiperRef.current.navigation.init();
            swiperRef.current.navigation.update();
        }

        handleSwiperStateUpdates(swiperRef.current);
    }, [vehiclesData.length, showRelatedSearchesTile, handleSwiperStateUpdates]);

    if (isLoading) {
        return <CircularProgress color="secondary" size={64} />;
    }

    return (
        <StyledBox sx={{ maxWidth: '1408px', margin: '0 auto' }}>
            <div
                id={carouselId}
                className="carousel--container"
                data-vehicle-count={vehicleCount}
                data-saleable-vehicle-count={saleableVehicleCount}
            >
                <div className={`carousel--grid ${isInsideHomeBase ? 'is-inside-homebase' : ''}`}>
                    {/* Would normally put mt on carousel, or use a stack, but too much legacy styling that I'm not sure if we need or not
                    and I don't want to break things */}
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography
                            color="secondary"
                            sx={{
                                whiteSpace: 'nowrap',
                                typography: { xs: 'h5', sm: 'h4' },
                            }}
                        >
                            {title}
                        </Typography>
                        <HeaderButtonsStack sx={{ display: { xs: 'none', sm: 'flex' } }} direction="row" spacing={2}>
                            {showButton && (
                                <StyledButton
                                    href={buttonHref}
                                    onClick={handleShowMoreButtonTagging}
                                    aria-label={buttonText}
                                    data-ignore-anchor-tag-rule
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                >
                                    {buttonText}
                                </StyledButton>
                            )}
                            <NavContainer
                                sx={{
                                    display: { sm: 'none', md: showNavigation ? 'flex' : 'none' },
                                }}
                            >
                                {/* Removing refs in favor of ids can result in skipping slides
                                 if the contents of the next group are not an exact multiple of slidesPerGroup */}
                                <span onClickCapture={() => handleArrowButtonTagging('previous')}>
                                    <IconButton
                                        variant="outlined"
                                        aria-label="previous slides"
                                        disabled={isPrevDisabled}
                                        ref={prevButton}
                                    >
                                        <KmxChevronLeftFilled />
                                    </IconButton>
                                </span>
                                <span onClickCapture={() => handleArrowButtonTagging('next')}>
                                    <IconButton
                                        variant="outlined"
                                        aria-label="next slides"
                                        ref={nextButton}
                                        disabled={isNextDisabled}
                                    >
                                        <KmxChevronRightFilled />
                                    </IconButton>
                                </span>
                            </NavContainer>
                        </HeaderButtonsStack>
                    </Stack>
                    <BaseCarousel
                        name={carouselId}
                        leftAligned={true}
                        nextFocus={`#${nextFocus}`}
                        ariaLabel={carouselId}
                        disableNavButtons={disableNavButtons}
                        onSwiper={handleSwiper}
                        onSlideChange={handleSlideChange}
                        navigationParams={navigationParams}
                        onBeforeInit={handleSwiperBeforeInit}
                    >
                        {carouselItems}
                    </BaseCarousel>
                </div>
            </div>
        </StyledBox>
    );
};

export default UftCarTilesCarousel;
